import type { ReactNode } from 'react';
import { CircleAlert, Eye, EyeOff, MessagesSquare, ShieldCheck, Sparkles, Wallet } from 'lucide-react';
import { cn } from '@hrms/ui';
import { BrandMark } from '../../components/BrandMark.js';
import { ThemeToggle } from '../../components/ThemeToggle.js';

/** Every claim here is something the product actually does (see CLAUDE.md) — this panel is the
 *  first thing a prospective customer reads, so it says nothing the product doesn't back up. */
const HIGHLIGHTS = [
  {
    icon: Wallet,
    title: 'Payroll for India and beyond',
    body: 'Statutory payroll per legal entity, with every salary change effective-dated.',
  },
  {
    icon: Sparkles,
    title: 'An HR copilot that asks first',
    body: 'It works within your permissions, and every change waits for your approval.',
  },
  {
    icon: MessagesSquare,
    title: 'Chat, approvals and attendance',
    body: 'One workspace for your people — no extra tools to stitch together.',
  },
  {
    icon: ShieldCheck,
    title: 'Sensitive data, sealed',
    body: 'Aadhaar, PAN and bank details are encrypted with AES-256-GCM.',
  },
] as const;

/** The mark plus the wordmark. `inverse` is for the gradient panel, where the accent would vanish. */
function BrandLockup({ inverse = false, className }: { inverse?: boolean; className?: string }) {
  return (
    <div className={cn('flex items-center gap-3', className)}>
      <BrandMark
        size={36}
        className={inverse ? 'ring-1 ring-fg-on-accent/25' : 'shadow-accent'}
      />
      <span
        className={cn(
          'font-display text-xl font-bold tracking-tight',
          inverse ? 'text-fg-on-accent' : 'text-fg',
        )}
      >
        People<span className={inverse ? 'text-fg-on-accent/70' : 'text-accent'}>Orbit</span>
      </span>
    </div>
  );
}

/** Slowly turning orbit rings behind the brand panel's copy. Decoration on a screen a person sees
 *  once a session, not every few seconds — and tokens.css's reduced-motion rule stills it. */
function OrbitRings() {
  const rings = [
    { r: 150, duration: '70s', dot: 0 },
    { r: 230, duration: '110s', dot: 140 },
    { r: 310, duration: '160s', dot: 250 },
  ];
  return (
    <svg
      viewBox="-340 -340 680 680"
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-56 -right-56 size-[42rem] text-fg-on-accent"
    >
      {rings.map(({ r, duration, dot }) => (
        <g
          key={r}
          className="origin-center [transform-box:fill-box] motion-safe:animate-spin"
          style={{ animationDuration: duration }}
        >
          <circle r={r} fill="none" stroke="currentColor" strokeOpacity="0.14" />
          <circle
            cx={r * Math.cos((dot * Math.PI) / 180)}
            cy={r * Math.sin((dot * Math.PI) / 180)}
            r="5"
            fill="currentColor"
            fillOpacity="0.55"
          />
        </g>
      ))}
      <circle r="70" fill="currentColor" fillOpacity="0.06" />
    </svg>
  );
}

function BrandPanel() {
  return (
    <aside className="relative hidden w-[44%] max-w-[40rem] shrink-0 flex-col justify-between overflow-hidden bg-[image:var(--hero-gradient)] p-12 text-fg-on-accent lg:flex xl:p-16">
      <div className="auth-dots pointer-events-none absolute inset-0 opacity-15" aria-hidden="true" />
      <div
        className="pointer-events-none absolute -left-24 -top-24 size-96 rounded-full bg-fg-on-accent/10 blur-3xl"
        aria-hidden="true"
      />
      <OrbitRings />

      <BrandLockup inverse className="relative" />

      <div className="relative max-w-md">
        <p className="font-display text-4xl font-bold leading-[1.1] tracking-tight xl:text-5xl">
          Your whole people operation, in one orbit.
        </p>
        <p className="mt-4 text-md leading-relaxed text-fg-on-accent/75">
          Payroll, attendance, leave, chat and an AI copilot — one workspace for every company you
          run.
        </p>

        <ul className="mt-10 flex flex-col gap-5">
          {HIGHLIGHTS.map(({ icon: Icon, title, body }) => (
            <li key={title} className="flex gap-4">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-fg-on-accent/12 ring-1 ring-fg-on-accent/20 backdrop-blur-sm">
                <Icon className="size-[1.125rem]" aria-hidden="true" />
              </span>
              <span className="flex flex-col gap-0.5">
                <span className="text-base font-semibold">{title}</span>
                <span className="text-sm leading-relaxed text-fg-on-accent/70">{body}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      <p className="relative text-xs text-fg-on-accent/60">
        © {new Date().getFullYear()} PeopleOrbit
      </p>
    </aside>
  );
}

/**
 * The frame every unauthenticated screen renders in — login, signup, and everything in the
 * password/verification lifecycle. Large screens split into a branded panel and the form; below
 * `lg` the panel drops away and the form stands alone under the logo.
 *
 * Carries the theme toggle too: it's the one element every pre-auth screen renders, so a
 * signed-out visitor gets light/dark control without each screen adding it separately.
 */
export function AuthLayout({
  children,
  width = 'md',
}: {
  children: ReactNode;
  /** `lg` for the longer two-column signup form. */
  width?: 'md' | 'lg';
}) {
  return (
    <div className="flex min-h-dvh bg-bg">
      <BrandPanel />

      <main className="auth-backdrop relative flex min-w-0 flex-1 flex-col">
        <div className="absolute right-4 top-4 z-10 sm:right-6 sm:top-6">
          <ThemeToggle className="flex size-9 items-center justify-center rounded-full border border-border bg-surface/80 text-fg-muted shadow-sm backdrop-blur transition-colors hover:text-fg" />
        </div>

        <div className="flex flex-1 flex-col items-center justify-center px-4 py-14 sm:px-8">
          <div
            className={cn(
              'w-full duration-500 ease-out animate-in fade-in slide-in-from-bottom-3',
              width === 'lg' ? 'max-w-xl' : 'max-w-[26rem]',
            )}
          >
            <BrandLockup className="mb-8 justify-center lg:hidden" />
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}

/** The raised surface a form or a status message sits on. */
export function AuthCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <section
      className={cn(
        'rounded-2xl border border-border bg-surface p-6 shadow-card sm:p-8',
        className,
      )}
    >
      {children}
    </section>
  );
}

const ICON_TONES = {
  accent: 'bg-accent-subtle text-accent ring-accent/15',
  success: 'bg-success-subtle text-success ring-success/15',
  warning: 'bg-warning-subtle text-warning ring-warning/15',
  danger: 'bg-danger-subtle text-danger ring-danger/15',
} as const;

/** Title + supporting line atop a card, with an optional icon badge. Status screens centre it. */
export function AuthHeading({
  title,
  description,
  icon,
  tone = 'accent',
  align = 'left',
  tight = false,
  oneLineTitle = false,
}: {
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  tone?: keyof typeof ICON_TONES;
  align?: 'left' | 'center';
  /** Less space below, for a heading that a link or control sits directly under. */
  tight?: boolean;
  /**
   * Keep the title on one line: it scales down with the screen width (never below ~17px) instead of
   * wrapping on a phone. For a long title that would otherwise split in two.
   */
  oneLineTitle?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex flex-col',
        tight ? 'mb-2' : 'mb-7',
        align === 'center' && 'items-center text-center',
      )}
    >
      {icon && (
        <span
          className={cn(
            'mb-5 flex size-12 items-center justify-center rounded-2xl ring-4',
            ICON_TONES[tone],
          )}
          aria-hidden="true"
        >
          {icon}
        </span>
      )}
      <h1
        className={cn(
          'font-display font-bold tracking-tight text-fg',
          oneLineTitle ? 'whitespace-nowrap text-[length:clamp(1.05rem,5.4vw,1.5rem)]' : 'text-2xl',
        )}
      >
        {title}
      </h1>
      {description && (
        <p className="mt-2 text-base leading-relaxed text-fg-muted">{description}</p>
      )}
    </div>
  );
}

/** A form-level error, announced on arrival. */
export function AuthAlert({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="flex items-start gap-2.5 rounded-lg border border-danger/20 bg-danger-subtle px-3.5 py-2.5 text-sm text-danger duration-200 animate-in fade-in slide-in-from-top-1"
    >
      <CircleAlert className="mt-px size-4 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

/** The show/hide button that sits in a password field's trailing slot. */
export function PasswordToggle({
  shown,
  onToggle,
  className,
}: {
  shown: boolean;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={shown ? 'Hide password' : 'Show password'}
      aria-pressed={shown}
      className={cn(
        'flex size-8 items-center justify-center rounded-md text-fg-subtle transition-colors hover:bg-bg-subtle hover:text-fg-muted',
        className,
      )}
      onClick={onToggle}
    >
      {shown ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
    </button>
  );
}
